import React, {
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { Button } from "@/components/ui/Button";
import { useVideoPlayer } from "@/hooks/useVideoPlayer";
import { cn } from "@/utils/cn";

export interface InlineVideoPlayerHandle {
  /** Toggle play/pause on the underlying <video> element. */
  togglePlay: () => void;
  play: () => void;
  pause: () => void;
}

interface InlineVideoPlayerProps {
  mediaItem: MediaItem;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  className?: string;
  objectFit?: "cover" | "contain";
  getStreamUrlByFileId?: (fileId: string) => string | undefined;
  skipHeal?: boolean;
  /** Notified with the <video>'s paused state whenever it plays or pauses. */
  onPlayStateChange?: (paused: boolean) => void;
}

export const InlineVideoPlayer = forwardRef<
  InlineVideoPlayerHandle,
  InlineVideoPlayerProps
>(function InlineVideoPlayer(
  {
    mediaItem,
    autoPlay = false,
    loop = false,
    muted = false,
    controls = true,
    className,
    objectFit = "cover",
    getStreamUrlByFileId,
    skipHeal = false,
    onPlayStateChange,
  },
  ref,
) {
  const {
    initialize,
    retry,
    videoRef,
    hasError,
    userMessage,
    showSpinner,
  } = useVideoPlayer({
    getStreamUrlByFileId,
    autoPlay,
    skipHeal,
  });

  useEffect(() => {
    if (mediaItem && !mediaItem.isProcessing()) {
      initialize(mediaItem);
    }
  }, [mediaItem, initialize]);

  // iOS WebKit's muted-autoplay gate checks the `muted` *attribute*, not
  // the property React sets via the `muted` prop. Same story for
  // `playsinline`. Set them imperatively on mount so mobile browsers
  // permit autoplay.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (muted) {
      video.muted = true;
      video.setAttribute("muted", "");
    }
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
  }, [muted, videoRef]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } else {
      video.pause();
    }
  }, [videoRef]);

  useImperativeHandle(
    ref,
    () => ({
      togglePlay,
      play: () => {
        const video = videoRef.current;
        if (!video) return;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      },
      pause: () => videoRef.current?.pause(),
    }),
    [togglePlay, videoRef],
  );

  // Mirror the element's play/pause state to the parent. With native controls
  // hidden in the carousel, this is the only paused signal for the indicator.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const sync = () => onPlayStateChange?.(video.paused);
    video.addEventListener("play", sync);
    video.addEventListener("pause", sync);
    return () => {
      video.removeEventListener("play", sync);
      video.removeEventListener("pause", sync);
    };
  }, [onPlayStateChange, videoRef, mediaItem]);

  return (
    <div className={cn("relative flex items-center justify-center w-full h-full", className)}>
      <video
        ref={videoRef}
        className="w-full h-full relative z-10"
        controls={controls}
        controlsList="nodownload"
        playsInline
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        preload="auto"
        style={{
          objectFit,
          opacity: showSpinner || hasError || mediaItem.isProcessing() ? 0 : 1,
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        Your browser does not support the video tag.
      </video>

      {/* Processing State */}
      {mediaItem.isProcessing() && (
        <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
          <div className="text-center p-8">
            <div className="relative mb-4">
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-purple-700 border-t-purple-400 mx-auto"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl">🎬</span>
              </div>
            </div>
            <h3 className="text-white text-lg font-medium mb-2">
              Processing Video
            </h3>
            <p className="text-gray-400 text-sm px-4">
              This video is being processed and will be ready soon.
            </p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {!mediaItem.isProcessing() && showSpinner && (
        <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-700 border-t-white mx-auto"></div>
            </div>
            <p className="text-white mt-4 text-sm px-4">
              {userMessage || "Loading video..."}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {!mediaItem.isProcessing() && hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
          <div className="text-center p-8">
            <div className="text-red-500 mb-4">
              <svg
                className="w-20 h-20 mx-auto"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-white text-lg font-medium mb-2">
              Unable to Load Video
            </h3>
            <p className="text-gray-400 text-sm mb-6 px-4">
              {userMessage || "An error occurred"}
            </p>
            <Button
              onClick={retry}
              variant="primary"
              className="bg-white text-black hover:bg-gray-200"
            >
              Try Again
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

InlineVideoPlayer.displayName = "InlineVideoPlayer";
